import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Moon,
    Star,
    CheckCircle,
    Target,
    TrendingUp,
    MessageSquare,
    Calendar,
    Save,
    ChevronRight,
    Sparkles,
    History,
    ChevronDown,
    ChevronUp,
} from 'lucide-react';
import { useAppState } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { saveDebriefFacts } from '../../services/secondBrainService';

interface DayReflection {
    date: string;
    rating: number;
    wins: string[];
    improvements: string[];
    journal: string;
    gratitude: string[];
}

export default function AppEveningDebrief() {
    const navigate = useNavigate();
    const { tasks, events } = useAppState();
    const { user } = useAuth();

    const today = new Date().toISOString().split('T')[0];

    // View mode: 'today' or 'history'
    const [viewMode, setViewMode] = useState<'today' | 'history'>('today');

    // History state
    const [reflectionHistory, setReflectionHistory] = useState<DayReflection[]>([]);
    const [expandedDate, setExpandedDate] = useState<string | null>(null);

    // Load reflection history from localStorage
    const loadReflectionHistory = () => {
        const stored = localStorage.getItem('reflections');
        if (stored) {
            const reflections = JSON.parse(stored) as Record<string, DayReflection>;
            // Convert to array and sort by date (newest first)
            const historyArray = Object.values(reflections)
                .sort((a, b) => b.date.localeCompare(a.date));
            setReflectionHistory(historyArray);
        }
    };

    useEffect(() => {
        loadReflectionHistory();
    }, [viewMode]); // Reload when switching tabs

    // Listen for storage events (when AI saves a debrief)
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'reflections') {
                console.log('[EveningDebrief] Storage event detected, refreshing history');
                loadReflectionHistory();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Reflection state
    const [rating, setRating] = useState(0);
    const [wins, setWins] = useState<string[]>([]);
    const [newWin, setNewWin] = useState('');
    const [improvements, setImprovements] = useState<string[]>([]);
    const [newImprovement, setNewImprovement] = useState('');
    const [journal, setJournal] = useState('');
    const [gratitude, setGratitude] = useState<string[]>(['', '', '']);
    const [saved, setSaved] = useState(false);

    // Calculate today's stats
    const stats = useMemo(() => {
        const todayTasks = tasks.filter(t =>
            !t.deletedAt &&
            (t.dueDate === today || t.completedAt?.startsWith(today))
        );
        const completed = todayTasks.filter(t => t.completed);
        const incomplete = todayTasks.filter(t => !t.completed);

        const todayEvents = events.filter(e =>
            e.date === today && !e.deletedAt
        );

        // Find high-priority completions
        const highPriorityCompleted = completed.filter(t => t.priority === 'high');

        return {
            totalTasks: todayTasks.length,
            completed: completed.length,
            incomplete: incomplete.length,
            completionRate: todayTasks.length > 0
                ? Math.round((completed.length / todayTasks.length) * 100)
                : 100,
            eventsAttended: todayEvents.length,
            highPriorityCompleted: highPriorityCompleted.length,
            completedTasks: completed,
        };
    }, [tasks, events, today]);

    // Auto-populate wins from completed tasks
    const suggestedWins = stats.completedTasks.slice(0, 3).map(t => t.title);

    const handleAddWin = () => {
        if (newWin.trim()) {
            setWins([...wins, newWin.trim()]);
            setNewWin('');
        }
    };

    const handleAddWinFromSuggestion = (suggestion: string) => {
        if (!wins.includes(suggestion)) {
            setWins([...wins, suggestion]);
        }
    };

    const handleAddImprovement = () => {
        if (newImprovement.trim()) {
            setImprovements([...improvements, newImprovement.trim()]);
            setNewImprovement('');
        }
    };

    const handleGratitudeChange = (index: number, value: string) => {
        const newGratitude = [...gratitude];
        newGratitude[index] = value;
        setGratitude(newGratitude);
    };

    const handleSave = () => {
        // In a real app, save to state/backend
        const reflection: DayReflection = {
            date: today,
            rating,
            wins,
            improvements,
            journal,
            gratitude: gratitude.filter(g => g.trim()),
        };

        // Store in localStorage for now
        const reflections = JSON.parse(localStorage.getItem('reflections') || '{}');
        reflections[today] = reflection;
        localStorage.setItem('reflections', JSON.stringify(reflections));

        // AUTO-CAPTURE: Memorize debrief in Second Brain
        if (user?.id) {
            console.log('[EveningDebrief] Auto-capturing debrief to Second Brain');
            saveDebriefFacts(user.id, {
                ...reflection,
                rating // Already in reflection
            }).catch(err => console.error('[EveningDebrief] Second Brain capture failed:', err));
        }

        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
    };

    const formatDate = (date: Date) => {
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="min-h-screen bg-[#F5F8FA] dark:bg-dark-bg pb-24">
            {/* Header */}
            <div className="bg-gradient-to-br from-[#1A2832] to-[#2A3842] text-white px-4 pt-6 pb-8">
                <div className="flex items-center justify-between mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 -ml-2 hover:bg-white/10 rounded-full transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <button
                        onClick={handleSave}
                        className={`px-4 py-2 rounded-full font-medium transition-colors ${saved
                            ? 'bg-green-500 text-white'
                            : 'bg-white/20 hover:bg-white/30'
                            }`}
                    >
                        {saved ? '✓ Saved' : 'Save'}
                    </button>
                </div>

                {/* Header Content */}
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                        <Moon className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">Evening Debrief</h1>
                        <p className="text-white/80 text-sm">{formatDate(new Date())}</p>
                    </div>
                </div>

                {/* Day Stats */}
                <div className="grid grid-cols-3 gap-3 mt-4">
                    <div className="bg-white/10 rounded-xl p-3 text-center">
                        <CheckCircle className="w-5 h-5 mx-auto mb-1 text-green-400" />
                        <div className="text-lg font-bold">{stats.completed}</div>
                        <div className="text-xs text-white/70">Completed</div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-3 text-center">
                        <Target className="w-5 h-5 mx-auto mb-1 text-amber-400" />
                        <div className="text-lg font-bold">{stats.completionRate}%</div>
                        <div className="text-xs text-white/70">Rate</div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-3 text-center">
                        <Calendar className="w-5 h-5 mx-auto mb-1 text-blue-400" />
                        <div className="text-lg font-bold">{stats.eventsAttended}</div>
                        <div className="text-xs text-white/70">Events</div>
                    </div>
                </div>
            </div>

            {/* Tab Switcher */}
            <div className="px-4 -mt-2 mb-2">
                <div className="bg-white dark:bg-dark-card rounded-xl p-1 flex shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                    <button
                        onClick={() => setViewMode('today')}
                        className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${viewMode === 'today'
                            ? 'bg-[#0D5F6B] text-white'
                            : 'text-[#4A5C6A] hover:bg-gray-100'
                            }`}
                    >
                        <Moon className="w-4 h-4" />
                        Today's Debrief
                    </button>
                    <button
                        onClick={() => setViewMode('history')}
                        className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${viewMode === 'history'
                            ? 'bg-[#0D5F6B] text-white'
                            : 'text-[#4A5C6A] hover:bg-gray-100'
                            }`}
                    >
                        <History className="w-4 h-4" />
                        History
                    </button>
                </div>
            </div>

            {viewMode === 'today' ? (
                <div className="p-4 space-y-4">
                    {/* Day Rating */}
                    <div className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                        <h3 className="font-semibold text-[#1A2832] dark:text-white mb-3">How was your day?</h3>
                        <div className="flex justify-center gap-2">
                            {[1, 2, 3, 4, 5].map(star => (
                                <button
                                    key={star}
                                    onClick={() => setRating(star)}
                                    className="p-2"
                                >
                                    <Star
                                        className={`w-8 h-8 transition-colors ${star <= rating
                                            ? 'text-amber-400 fill-amber-400'
                                            : 'text-gray-300'
                                            }`}
                                    />
                                </button>
                            ))}
                        </div>
                        <p className="text-center text-sm text-[#7B8C9B] mt-2">
                            {rating === 0 && 'Tap to rate'}
                            {rating === 1 && 'Tough day - tomorrow will be better!'}
                            {rating === 2 && 'Not great, but you showed up'}
                            {rating === 3 && 'Solid day with room to grow'}
                            {rating === 4 && 'Great day! Keep it up'}
                            {rating === 5 && 'Amazing! You crushed it! 🎉'}
                        </p>
                    </div>

                    {/* Wins */}
                    <div className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                        <div className="flex items-center gap-2 mb-3">
                            <TrendingUp className="w-5 h-5 text-green-500" />
                            <h3 className="font-semibold text-[#1A2832] dark:text-white">Today's Wins</h3>
                        </div>

                        {/* Suggested wins from completed tasks */}
                        {suggestedWins.length > 0 && wins.length < 3 && (
                            <div className="mb-3">
                                <p className="text-xs text-[#7B8C9B] dark:text-dark-text-muted mb-2">Quick add from completed tasks:</p>
                                <div className="flex flex-wrap gap-2">
                                    {suggestedWins.map((suggestion, i) => (
                                        <button
                                            key={i}
                                            onClick={() => handleAddWinFromSuggestion(suggestion)}
                                            disabled={wins.includes(suggestion)}
                                            className={`px-3 py-1 text-sm rounded-full border transition-colors ${wins.includes(suggestion)
                                                ? 'bg-green-50 border-green-200 text-green-600'
                                                : 'border-[#DCE4EA] hover:border-[#0D5F6B] hover:text-[#0D5F6B]'
                                                }`}
                                        >
                                            + {suggestion.slice(0, 25)}{suggestion.length > 25 ? '...' : ''}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Entered wins */}
                        {wins.length > 0 && (
                            <div className="space-y-2 mb-3">
                                {wins.map((win, i) => (
                                    <div key={i} className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
                                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                        <span className="text-sm text-[#1A2832]">{win}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Add new win */}
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={newWin}
                                onChange={(e) => setNewWin(e.target.value)}
                                placeholder="Add a win..."
                                className="flex-1 px-3 py-2 border border-[#DCE4EA] rounded-xl text-sm outline-none focus:border-[#0D5F6B]"
                                onKeyDown={(e) => e.key === 'Enter' && handleAddWin()}
                            />
                            <button
                                onClick={handleAddWin}
                                className="px-4 py-2 bg-[#0D5F6B] text-white rounded-xl font-medium"
                            >
                                Add
                            </button>
                        </div>
                    </div>

                    {/* Areas for Improvement */}
                    <div className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5 text-purple-500" />
                            <h3 className="font-semibold text-[#1A2832] dark:text-white">Tomorrow I'll...</h3>
                        </div>

                        {improvements.length > 0 && (
                            <div className="space-y-2 mb-3">
                                {improvements.map((item, i) => (
                                    <div key={i} className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg">
                                        <ChevronRight className="w-4 h-4 text-purple-500 flex-shrink-0" />
                                        <span className="text-sm text-[#1A2832]">{item}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={newImprovement}
                                onChange={(e) => setNewImprovement(e.target.value)}
                                placeholder="What will you do differently?"
                                className="flex-1 px-3 py-2 border border-[#DCE4EA] rounded-xl text-sm outline-none focus:border-[#0D5F6B]"
                                onKeyDown={(e) => e.key === 'Enter' && handleAddImprovement()}
                            />
                            <button
                                onClick={handleAddImprovement}
                                className="px-4 py-2 bg-[#0D5F6B] text-white rounded-xl font-medium"
                            >
                                Add
                            </button>
                        </div>
                    </div>

                    {/* Gratitude */}
                    <div className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                        <div className="flex items-center gap-2 mb-3">
                            <Star className="w-5 h-5 text-amber-500" />
                            <h3 className="font-semibold text-[#1A2832] dark:text-white">3 Things I'm Grateful For</h3>
                        </div>

                        <div className="space-y-2">
                            {[0, 1, 2].map(i => (
                                <div key={i} className="flex items-center gap-2">
                                    <span className="text-amber-500 font-bold">{i + 1}.</span>
                                    <input
                                        type="text"
                                        value={gratitude[i]}
                                        onChange={(e) => handleGratitudeChange(i, e.target.value)}
                                        placeholder={`Gratitude ${i + 1}...`}
                                        className="flex-1 px-3 py-2 border border-[#DCE4EA] rounded-xl text-sm outline-none focus:border-[#0D5F6B]"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Journal Entry */}
                    <div className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                        <div className="flex items-center gap-2 mb-3">
                            <MessageSquare className="w-5 h-5 text-[#0D5F6B]" />
                            <h3 className="font-semibold text-[#1A2832] dark:text-white">Journal</h3>
                        </div>

                        <textarea
                            value={journal}
                            onChange={(e) => setJournal(e.target.value)}
                            placeholder="How are you feeling? What's on your mind? Any thoughts you want to capture..."
                            className="w-full p-3 border border-[#DCE4EA] rounded-xl text-sm outline-none focus:border-[#0D5F6B] min-h-[120px] resize-none"
                        />
                    </div>

                    {/* Save Button */}
                    <button
                        onClick={handleSave}
                        className={`w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors ${saved
                            ? 'bg-green-500 text-white'
                            : 'bg-[#0D5F6B] text-white'
                            }`}
                    >
                        <Save className="w-5 h-5" />
                        {saved ? 'Reflection Saved!' : 'Save Reflection'}
                    </button>
                </div>
            ) : (
                /* History View */
                <div className="p-4 space-y-4">
                    {reflectionHistory.length === 0 ? (
                        <div className="bg-white dark:bg-dark-card rounded-2xl p-8 text-center shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                            <History className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                            <h3 className="font-semibold text-[#1A2832] dark:text-white mb-2">No Past Reflections</h3>
                            <p className="text-sm text-[#7B8C9B]">Complete today's debrief to start building your reflection history.</p>
                        </div>
                    ) : (
                        reflectionHistory.map((reflection) => (
                            <div
                                key={reflection.date}
                                className="bg-white dark:bg-dark-card rounded-2xl shadow-[0_4px_16px_rgba(13,95,107,0.08)] overflow-hidden"
                            >
                                {/* Reflection Header - Always Visible */}
                                <button
                                    onClick={() => setExpandedDate(expandedDate === reflection.date ? null : reflection.date)}
                                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-dark-border transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-[#0D5F6B]/10 rounded-full flex items-center justify-center">
                                            <Moon className="w-5 h-5 text-[#0D5F6B]" />
                                        </div>
                                        <div className="text-left">
                                            <div className="font-semibold text-[#1A2832] dark:text-white">
                                                {new Date(reflection.date + 'T00:00:00').toLocaleDateString('en-US', {
                                                    weekday: 'short',
                                                    month: 'short',
                                                    day: 'numeric'
                                                })}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                {[1, 2, 3, 4, 5].map(star => (
                                                    <Star
                                                        key={star}
                                                        className={`w-3 h-3 ${star <= reflection.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-[#7B8C9B]">{reflection.wins.length} wins</span>
                                        {expandedDate === reflection.date ? (
                                            <ChevronUp className="w-5 h-5 text-[#7B8C9B]" />
                                        ) : (
                                            <ChevronDown className="w-5 h-5 text-[#7B8C9B]" />
                                        )}
                                    </div>
                                </button>

                                {/* Expanded Content */}
                                {expandedDate === reflection.date && (
                                    <div className="px-4 pb-4 space-y-4 border-t border-gray-100 dark:border-dark-border">
                                        {/* Wins */}
                                        {reflection.wins.length > 0 && (
                                            <div className="pt-4">
                                                <h4 className="text-sm font-medium text-[#4A5C6A] mb-2 flex items-center gap-2">
                                                    <TrendingUp className="w-4 h-4 text-green-500" />
                                                    Wins
                                                </h4>
                                                <div className="space-y-1">
                                                    {reflection.wins.map((win, i) => (
                                                        <div key={i} className="flex items-center gap-2 text-sm text-[#1A2832] dark:text-white">
                                                            <CheckCircle className="w-3 h-3 text-green-500" />
                                                            {win}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Improvements */}
                                        {reflection.improvements.length > 0 && (
                                            <div>
                                                <h4 className="text-sm font-medium text-[#4A5C6A] mb-2 flex items-center gap-2">
                                                    <Sparkles className="w-4 h-4 text-purple-500" />
                                                    Goals for Tomorrow
                                                </h4>
                                                <div className="space-y-1">
                                                    {reflection.improvements.map((item, i) => (
                                                        <div key={i} className="flex items-center gap-2 text-sm text-[#1A2832] dark:text-white">
                                                            <ChevronRight className="w-3 h-3 text-purple-500" />
                                                            {item}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Gratitude */}
                                        {reflection.gratitude.filter(g => g.trim()).length > 0 && (
                                            <div>
                                                <h4 className="text-sm font-medium text-[#4A5C6A] mb-2 flex items-center gap-2">
                                                    <Star className="w-4 h-4 text-amber-500" />
                                                    Gratitude
                                                </h4>
                                                <div className="space-y-1">
                                                    {reflection.gratitude.filter(g => g.trim()).map((item, i) => (
                                                        <div key={i} className="text-sm text-[#1A2832] dark:text-white">
                                                            {i + 1}. {item}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Journal */}
                                        {reflection.journal && (
                                            <div>
                                                <h4 className="text-sm font-medium text-[#4A5C6A] mb-2 flex items-center gap-2">
                                                    <MessageSquare className="w-4 h-4 text-[#0D5F6B]" />
                                                    Journal
                                                </h4>
                                                <p className="text-sm text-[#1A2832] dark:text-white whitespace-pre-wrap">
                                                    {reflection.journal}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
